import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { cloneDeep, merge, uniq } from 'lodash'
import { isEqual } from 'lodash'

import { AppDispatch, GetState, RootState } from '../../store'
import { listenerMiddleware } from '../../store/listenerMiddleware'

import {
  getRelated,
  hydrateConversation,
  isConversationInFilter,
  loadSessionsList
} from './helpers'
import { hydrateMessage, uniqueArray, arrayDifference } from '../../utilities'
import {
  DEFAULT_SESSION_EMBEDS,
  arrayToQuery,
  embedsToQuery,
  fetchUserTasks,
  getChannelConflicts
} from '../../lib/api'
import { selectViews } from '../views'
import { selectBaseInbox } from '../baseInbox'
import { selectAccess } from '../accessSlice'

export const TOP_FILTERS = {
  ALL: 'All',
  ASSIGNED_TO_ME: 'Assigned to me',
  OFFLINE: 'Offline',
  TRANSFERS: 'Transfers'
}

export enum MobileScreen {
  Queue,
  Conversation,
  EngagementHub
}

type APIChannelConflict = {
  id: string
  channel: APIChannel
  user: APISimpleUser
}

type ChannelConflict = {
  user: APISimpleUser
  channels: APIChannel[]
}

type SessionStore = Record<string, LocalSession>

export type ConvoState = {
  topFilter: string
  inbox: string | null
  view: string | null
  hideEngagementHub: boolean
  unreadCache: UnreadCache
  conversations: SessionStore
  loadingIDs: string[]
  allConversations: SessionStore
  allConversationsLoaded: boolean
  dataFetched: boolean
  mobileScreen: MobileScreen
  conflicts: { [x: string]: ChannelConflict[] }
  ownersFetched: string[]
  fetchedClosed: string[]
  updated: Record<string, APISession & { messages: any[] }>
  lastSelected: string
}

const initialState: ConvoState = {
  topFilter: TOP_FILTERS.ALL,
  inbox: null,
  view: null,
  hideEngagementHub: false,
  unreadCache: {},
  conversations: {},
  loadingIDs: [],
  allConversations: {},
  allConversationsLoaded: false,
  dataFetched: false,
  mobileScreen: MobileScreen.Queue,
  conflicts: {},
  ownersFetched: [],
  // List of inbox ids that have had closed sessions fetched
  fetchedClosed: [],
  updated: {},
  lastSelected: ''
}

const convoSlice = createSlice({
  name: 'convo',
  initialState,
  reducers: {
    startFetch: (state: ConvoState) => {
      state.dataFetched = false
      state.conversations = {}
      state.loadingIDs = []
    },

    endFetch: (state: ConvoState, action: PayloadAction<SessionStore>) => {
      state.conversations = action.payload
      state.dataFetched = true
      state.loadingIDs = Object.keys(action.payload)
    },

    setFilteredConvos: (
      state: ConvoState,
      action: PayloadAction<SessionStore>
    ) => {
      state.conversations = action.payload
    },

    setTopFilter: (state: ConvoState, action: PayloadAction<string>) => {
      state.topFilter = action.payload
    },

    setInbox: (state: ConvoState, action: PayloadAction<string | null>) => {
      state.inbox = action.payload
    },

    setView: (state: ConvoState, action: PayloadAction<string | null>) => {
      state.view = action.payload
    },

    updateCache: (
      state: ConvoState,
      action: PayloadAction<{
        id: string
        count: number
        userID: string
        view: APIView | null
      }>
    ) => {
      const { id, count, userID, view } = action.payload
      const { topFilter, inbox } = state

      // Wait for updates to settle
      if (!state.dataFetched) {
        return
      }

      // Check for valid filtering
      if (!topFilter || !inbox) {
        return
      }

      // Update cache object to have correct shape
      if (!(topFilter in state.unreadCache)) {
        state.unreadCache[topFilter] = {}
      }
      if (!(inbox in state.unreadCache[topFilter])) {
        state.unreadCache[topFilter][inbox] = {}
      }

      const storedConversation = state.allConversations[id]
      if (!storedConversation) {
        return
      }

      if (
        isConversationInFilter(
          storedConversation,
          topFilter,
          inbox,
          userID,
          view
        )
      ) {
        // Store count
        state.unreadCache[topFilter][inbox][id] = count
      }
    },

    updateCache2: (state: ConvoState, action: PayloadAction<UnreadCache>) => {
      state.unreadCache = merge(state.unreadCache, action.payload)
    },

    setMobileScreen: (
      state: ConvoState,
      action: PayloadAction<MobileScreen>
    ) => {
      state.mobileScreen = action.payload
    },

    updateConflicts: (
      state: ConvoState,
      action: PayloadAction<{ id: string; conflicts: ChannelConflict[] }>
    ) => {
      const { id, conflicts } = action.payload
      state.conflicts[id] = conflicts
    },

    finishLoadingAll: (
      state: ConvoState,
      action: PayloadAction<SessionStore>
    ) => {
      state.allConversations = action.payload
      state.allConversationsLoaded = true
    },

    updateOwnerTasks: (state: ConvoState, action: PayloadAction<string>) => {
      state.ownersFetched.push(action.payload)
    },

    closeSession: (state: ConvoState, action: PayloadAction<string>) => {
      const id = action.payload
      if (id in state.allConversations) {
        state.allConversations[id].state = 'CLOSED'
      }
    },

    updateClosedFetch: (
      state: ConvoState,
      action: PayloadAction<{ inbox: string; closed: LocalSession[] }>
    ) => {
      const { inbox, closed } = action.payload
      state.fetchedClosed.push(inbox)
      closed.forEach(session => {
        state.allConversations[session.id] = session
      })
    },

    changeType: (
      state: ConvoState,
      action: PayloadAction<{ id: string; type: string }>
    ) => {
      const { id, type } = action.payload
      if (id in state.allConversations) {
        state.allConversations[id].type = type
      }
    },

    deleteConversation: (
      state: ConvoState,
      action: PayloadAction<string[]>
    ) => {
      action.payload.forEach(id => {
        delete state.allConversations[id]
      })
    },

    updateConversations: (
      state: ConvoState,
      action: PayloadAction<(convos: SessionStore) => SessionStore>
    ) => {
      state.allConversations = action.payload(cloneDeep(state.allConversations))
    },

    setNew: (state: ConvoState, action: PayloadAction<string>) => {
      const id = action.payload
      if (id in state.allConversations) {
        state.allConversations[id].isNew = false
      }
    },

    setFetched: (
      state: ConvoState,
      action: PayloadAction<{ id: string; fetched: boolean }>
    ) => {
      const { id, fetched } = action.payload
      if (id in state.allConversations) {
        state.allConversations[id].fetched = fetched
      }
    },

    setConversation: (
      state: ConvoState,
      action: PayloadAction<{ id: string; conversation: null | LocalSession }>
    ) => {
      const { id, conversation } = action.payload

      if (!conversation) {
        return
      }

      if (!(id in state.allConversations)) {
        state.allConversations[id] = conversation
        return
      }

      const oldConvo = state.allConversations[id]
      state.allConversations[id] = { ...oldConvo, ...conversation }
      state.allConversations[id].messages = uniq([
        ...(oldConvo.messages ?? []),
        ...(conversation.messages ?? [])
      ])
    },

    endConversation: (state: ConvoState, action: PayloadAction<string>) => {
      const id = action.payload

      if (!(id in state.allConversations)) {
        return
      }

      state.allConversations[id].endDate = new Date().toISOString()
      state.allConversations[id].ended_at = new Date().toISOString()
      state.allConversations[id].closed = true
    },

    insertMessage: (state: ConvoState, action: PayloadAction<any>) => {
      const session = action.payload.sessionId as string

      const toUpdate = getRelated(state, session)
      if (toUpdate.length === 0) {
        return
      }

      // New message properties
      const { action_type, content, sessionId, id, tags, stamp } =
        action.payload
      const senderObj = content.user ?? {}
      const sender = senderObj?.name ?? ''

      // Update each related session with this message
      toUpdate.forEach(sessionID => {
        const current = state.allConversations[sessionID]
        const currentMessageIDX = current.messages.findIndex(
          msg => msg.id === id
        )

        if (currentMessageIDX >= 0) {
          // Replace message case
          const prevMessage =
            state.allConversations[sessionID].messages[currentMessageIDX]

          // Update previous message with changed properties
          current.messages[currentMessageIDX] = {
            ...prevMessage,
            tags,
            content: content.body,
            meta: { ...(prevMessage.meta ?? {}), ...content.meta }
          }
        } else {
          // Add message case

          // Hydrate message
          const newMessage = {
            content: content.body,
            media: content.media,
            sender,
            senderFull: senderObj,
            meta: content.meta,
            session_id: sessionId,
            tags,
            type: action_type,
            id,
            date: stamp ? new Date(stamp) : new Date()
          }

          current.messages.unshift(newMessage)
        }
      })
    },

    insertEvent: (state: ConvoState, action: PayloadAction<any>) => {
      const session = action.payload.sessionId as string

      const toUpdate = getRelated(state, session)
      if (toUpdate.length === 0) {
        return
      }

      const hydrated = hydrateMessage(action.payload)

      // Update each related session with this message
      toUpdate.forEach(sessionID => {
        const current = state.allConversations[sessionID]
        current.messages.unshift(hydrated)
      })
    },

    insertNote: (state: ConvoState, action: PayloadAction<any>) => {
      const session = action.payload.sessionId as string

      const toUpdate = getRelated(state, session)
      if (toUpdate.length === 0) {
        return
      }

      const { content, sessionId, stamp, id, tags } = action.payload
      const hydrated = {
        content: content.body,
        sender: content.user.name,
        date: new Date(stamp),
        media: content.media,
        id,
        tags,
        senderFull: content.user,
        session_id: sessionId
      }

      // Update each related session with this note
      toUpdate.forEach(sessionID => {
        const current = state.allConversations[sessionID]
        current.notes.unshift(hydrated)
      })
    },

    addUsers: (
      state: ConvoState,
      action: PayloadAction<{ id: string; users: any[] }>
    ) => {
      const { id, users } = action.payload

      if (!(id in state.allConversations)) {
        return
      }

      const convo = state.allConversations[id]

      if (Array.isArray(convo.users)) {
        convo.users = uniqueArray(...convo.users, ...users)
      } else {
        convo.users = users
      }
    },

    removeUsers: (
      state: ConvoState,
      action: PayloadAction<{ id: string; users: any[] }>
    ) => {
      const { id, users } = action.payload

      if (!(id in state.allConversations)) {
        return
      }

      const convo = state.allConversations[id]

      if (Array.isArray(convo.users)) {
        convo.users = arrayDifference(convo.users, users)
      } else {
        convo.users = []
      }
    },

    trackUpdate: (
      state: ConvoState,
      action: PayloadAction<{
        id: string
        conversation: APISession & { messages: any[] }
      }>
    ) => {
      const { id, conversation } = action.payload
      state.updated[id] = conversation
    },

    updateConvoSingleMessage: (
      state: ConvoState,
      action: PayloadAction<{
        id: string
        index: number | undefined
        message: any
      }>
    ) => {
      const { id, index, message } = action.payload
      state.allConversations[id].messages[index] = { ...message }
    },

    updateConvoMessages: (
      state: ConvoState,
      action: PayloadAction<{ id: string; messages: any[] }>
    ) => {
      const { id, messages } = action.payload
      state.allConversations[id].messages = messages
      state.allConversations[id].isNew = true
    },

    updateSingleCovo: (
      state: ConvoState,
      action: PayloadAction<{ id: string; dm: any }>
    ) => {
      const { id, dm } = action.payload
      state.allConversations[id] = { ...dm }
    },

    updateEngagementHubVisibility: (
      state: ConvoState,
      action: PayloadAction<boolean>
    ) => {
      state.hideEngagementHub = action.payload
    },

    setLastSelected: (state: ConvoState, action: PayloadAction<string>) => {
      state.lastSelected = action.payload
    },
    resetLastSelected: (state: ConvoState) => {
      state.lastSelected = ''
    }
  }
})

export const {
  setTopFilter,
  setInbox,
  setView,
  updateCache,
  setMobileScreen,
  closeSession,
  changeType,
  deleteConversation,
  updateConversations,
  setNew,
  setFetched,
  setConversation,
  endConversation,
  insertMessage,
  insertEvent,
  insertNote,
  addUsers,
  removeUsers,
  trackUpdate,
  updateConvoSingleMessage,
  updateConvoMessages,
  updateSingleCovo,
  setFilteredConvos,
  updateEngagementHubVisibility,
  setLastSelected,
  resetLastSelected
} = convoSlice.actions
const {
  startFetch,
  endFetch,
  updateCache2,
  updateConflicts,
  finishLoadingAll,
  updateOwnerTasks,
  updateClosedFetch
} = convoSlice.actions

export const loadUnreadCounts = (userID: string) => {
  return async (dispatch: AppDispatch, getState: GetState) => {
    const state = getState()
    const { inboxes } = selectBaseInbox(state)
    const { allConversations } = selectConvo(state)
    const { views } = selectViews(state)

    const unreads: UnreadCache = {}

    Object.values(TOP_FILTERS).forEach(topFilter => {
      unreads[topFilter] = {}

      inboxes.forEach(inbox => {
        const inboxID = inbox.id
        unreads[topFilter][inboxID] = {}

        const inboxViews = views.filter(
          view => view.type === 'inbox' && view.item_id === inboxID
        )
        inboxViews.forEach(view => {
          unreads[topFilter][view.id] = {}
        })

        Object.values(allConversations).forEach(session => {
          // Check if this session should be in this top filter and inbox combination
          if (
            isConversationInFilter(session, topFilter, inboxID, userID, null)
          ) {
            unreads[topFilter][inboxID][session.id] = session.unread
          }

          // Find view unread counts for this session
          inboxViews.forEach(view => {
            // Check that this session is in this top filter and inbox/view combination
            if (
              isConversationInFilter(session, topFilter, inboxID, userID, view)
            ) {
              unreads[topFilter][view.id][session.id] = session.unread
            }
          })
        })
      })
    })

    dispatch(updateCache2(unreads))
  }
}

export const loadNewConversationsList = (userID: string) => {
  return async (dispatch: AppDispatch, getState: GetState) => {
    const state = getState()
    const { topFilter, inbox, allConversations, view, fetchedClosed } =
      selectConvo(state)
    const { views } = selectViews(state)

    const selectedView = views.find(v => v.id === view) ?? null

    const extraConvos: LocalSession[] = []
    if (selectedView?.filter.state?.includes('CLOSED')) {
      if (!fetchedClosed.includes(inbox)) {
        const embeds = embedsToQuery(DEFAULT_SESSION_EMBEDS)
        const query = `${embeds}&inbox_ids[]=${inbox}&state[]=CLOSED`
        const closed = await loadSessionsList(query)
        const mapped = closed.map(session =>
          hydrateConversation(session, false)
        )
        extraConvos.push(...mapped)
        dispatch(updateClosedFetch({ inbox, closed: mapped }))
      }
    }

    dispatch(startFetch())

    const conversations: SessionStore = {}

    const toCheck = [...Object.values(allConversations), ...extraConvos]
    toCheck.forEach(session => {
      if (
        isConversationInFilter(session, topFilter, inbox, userID, selectedView)
      ) {
        conversations[session.id] = session
      }
    })

    dispatch(endFetch(conversations))
  }
}

export const loadAllConversations = (cb?: (s: SessionStore) => void) => {
  return async (dispatch: AppDispatch, getState: GetState) => {
    try {
      const embeds = embedsToQuery(DEFAULT_SESSION_EMBEDS)

      const { inboxes } = selectBaseInbox(getState())
      const inboxIDs = inboxes.map(inbox => inbox.id)
      const inboxQuery = arrayToQuery('inbox_ids', inboxIDs)
      const stateQuery = arrayToQuery('state', [
        'ACTIVE',
        'IDLE',
        'ENDED',
        'OVERDUE'
        // Probably don't want to view rejected conversations in general
        // 'REJECTED'
        // Can't get closed here, would blow up the request size from ~500 to 4100+ sessions
        // 'CLOSED',
      ])
      const query = `${embeds}&${inboxQuery}&${stateQuery}`

      const allSessions = await loadSessionsList(query)

      const toSave: SessionStore = {}
      allSessions.forEach(session => {
        toSave[session.id] = hydrateConversation(session, false)
      })

      dispatch(finishLoadingAll(toSave))
      cb && cb(toSave)
    } catch (err) {
      console.error('Error loading all conversations', err)
    }
  }
}

export const checkConflicts = (owner: string) => {
  return async (dispatch: AppDispatch) => {
    try {
      if (!owner) {
        return
      }

      dispatch(updateConflicts({ id: owner, conflicts: [] }))

      const conflicts = (await getChannelConflicts(
        owner
      )) as APIChannelConflict[]

      const mappedConflicts: { [x: string]: ChannelConflict } = {}

      conflicts.forEach(conflict => {
        if (conflict.id) {
          const userID = conflict.user.id

          if (!(userID in mappedConflicts)) {
            mappedConflicts[userID] = {
              user: conflict.user,
              channels: []
            }
          }

          mappedConflicts[userID].channels.push(conflict.channel)
        }
      })

      dispatch(
        updateConflicts({
          id: owner,
          conflicts: Object.values(mappedConflicts)
        })
      )
    } catch {}
  }
}

export const fetchOwnerTasks = (
  owner: string,
  onComplete: (x: any) => void
) => {
  return async (dispatch: AppDispatch, getState: GetState) => {
    const { ownersFetched } = selectConvo(getState())
    if (ownersFetched.includes(owner)) {
      return
    }

    try {
      const tasks = await fetchUserTasks(owner)
      onComplete(tasks)
      dispatch(updateOwnerTasks(owner))
    } catch {}
  }
}

export const selectConvo = (state: RootState) => state.convo

export default convoSlice.reducer

listenerMiddleware.startListening({
  predicate: (action, currState, prevState) => {
    const { allConversations } = selectConvo(currState as RootState)
    const { allConversations: previousAllConversations } = selectConvo(
      prevState as RootState
    )

    return !isEqual(allConversations, previousAllConversations)
  },
  effect: (action, listenerApi) => {
    const state = listenerApi.getState() as RootState
    const { topFilter, allConversations, inbox, view } = selectConvo(state)
    const { user } = selectAccess(state)
    const { views } = selectViews(state)
    if (!user) return

    const selectedView = views.find(v => v.id === view) ?? null

    const conversations: SessionStore = {}

    const toCheck = [...Object.values(allConversations)]
    toCheck.forEach(session => {
      if (
        isConversationInFilter(
          session,
          topFilter,
          inbox,
          user?.id,
          selectedView
        )
      ) {
        conversations[session.id] = session
      }
    })
    listenerApi.dispatch(setFilteredConvos(conversations))
  }
})
